import { useEffect } from "react";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { ArrowLeft, LogOut, User as UserIcon, Mail, BadgeCheck, Venus, Mars } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { resolveAvatarUrl } from "@/lib/avatar";
import { useUserRole, type AppRole } from "@/hooks/use-user-role";
import { useCachedState } from "@/hooks/use-cached-state";
import { PasskeyManager } from "@/components/passkey-manager";

export const Route = createFileRoute("/_authenticated/profile")({
  component: ProfilePage,
});

const ROLE_LABEL: Record<AppRole, string> = {
  superadmin: "Super Admin",
  admin: "Admin",
  bendahara: "Bendahara",
  guru: "Guru",
};

type ProfileState = { name: string; email: string; avatar: string | null; gender: "L" | "P" | null };

function ProfilePage() {
  const navigate = useNavigate();
  const router = useRouter();
  const { role } = useUserRole();
  const [profile, setProfile] = useCachedState<ProfileState>("profile:me", {
    name: "",
    email: "",
    avatar: null,
    gender: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;
      const { data: prof } = await supabase
        .from("profiles")
        .select("full_name, avatar_url, email, gender")
        .eq("id", user.id)
        .maybeSingle();
      const url = await resolveAvatarUrl(prof?.avatar_url);
      if (cancelled) return;
      setProfile({
        name: prof?.full_name || user.email?.split("@")[0] || "Pengguna",
        email: prof?.email || user.email || "",
        avatar: url ?? null,
        gender: (prof as { gender?: "L" | "P" | null } | null)?.gender ?? null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [setProfile]);

  const logout = async () => {
    try { await supabase.rpc("mark_offline"); } catch { /* ignore */ }
    window.dispatchEvent(new Event("almunajah:account-reset"));
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  const initials =
    profile.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "U";

  const goBack = () => {
    if (window.history.length > 1) router.history.back();
    else navigate({ to: "/dashboard" });
  };

  return (
    <div className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6 lg:-mt-8 md:mx-0 md:mt-0">
      <div className="md:max-w-2xl md:mx-auto md:rounded-2xl md:overflow-hidden md:border md:bg-card md:shadow-sm">
        {/* Gradient header */}
        <div className="relative bg-gradient-to-br from-primary via-primary to-primary/85 text-primary-foreground px-5 pt-5 pb-16 md:pt-6">
          <button
            type="button"
            onClick={goBack}
            aria-label="Kembali"
            className="md:hidden absolute left-3 top-3 w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center active:scale-95 transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex flex-col items-center text-center pt-6 md:pt-2">
            <Avatar className="w-24 h-24 border-4 border-white/90 shadow-xl">
              {profile.avatar && <AvatarImage src={profile.avatar} />}
              <AvatarFallback className="bg-white text-primary text-2xl font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="mt-3 text-xl font-bold leading-tight px-2 break-words">{profile.name}</div>
            <div className="text-xs opacity-90 mt-1 break-all px-2">{profile.email}</div>
            {role && (
              <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full bg-white/20 backdrop-blur text-[11px] font-semibold border border-white/25">
                <BadgeCheck className="w-3 h-3" />
                {ROLE_LABEL[role]}
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-4 -mt-10 pb-6 space-y-3 relative z-10">
          <div className="rounded-2xl border bg-card shadow-sm divide-y overflow-hidden">
            <DetailRow
              icon={<UserIcon className="w-4.5 h-4.5" />}
              label="Nama Lengkap"
              value={profile.name || "—"}
              bold
            />
            <DetailRow
              icon={<Mail className="w-4.5 h-4.5" />}
              label="Email"
              value={profile.email || "—"}
              valueClassName="break-all"
            />
            {role && (
              <DetailRow
                icon={<BadgeCheck className="w-4.5 h-4.5" />}
                label="Peran"
                value={ROLE_LABEL[role]}
                valueClassName="text-primary font-semibold"
              />
            )}
            <DetailRow
              icon={
                profile.gender === "P" ? (
                  <Venus className="w-4.5 h-4.5" />
                ) : (
                  <Mars className="w-4.5 h-4.5" />
                )
              }
              label="Jenis Kelamin"
              value={
                profile.gender === "P"
                  ? "Perempuan"
                  : profile.gender === "L"
                  ? "Laki-laki"
                  : "—"
              }
            />
            <DetailRow
              icon={<span className="w-2 h-2 rounded-full bg-emerald-500" />}
              iconWrapClass="bg-emerald-100 text-emerald-600"
              label="Status"
              value="Aktif"
              valueClassName="text-emerald-600 font-semibold"
            />
          </div>

          <PasskeyManager />

          <Button
            onClick={logout}
            variant="outline"
            className="w-full h-12 gap-2 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive font-semibold"
          >
            <LogOut className="w-4 h-4" /> Keluar
          </Button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({
  icon,
  label,
  value,
  bold,
  valueClassName,
  iconWrapClass,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  bold?: boolean;
  valueClassName?: string;
  iconWrapClass?: string;
}) {
  return (
    <div className="flex items-center gap-3 px-3.5 py-3.5">
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
          iconWrapClass ?? "bg-primary/10 text-primary"
        }`}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] text-muted-foreground">{label}</div>
        <div
          className={`text-sm ${bold ? "font-semibold" : "font-medium"} ${
            valueClassName ?? ""
          }`}
        >
          {value}
        </div>
      </div>
    </div>
  );
}
